import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Controller, FieldPath, FieldValues } from "react-hook-form";
import Select from "react-select";

import Button, { ButtonTypes } from "../../Button";
import {
  DiscoveredListing,
  DiscoveredListings,
  isCatalogueSearchResult,
  InstrumentSearchResult,
  ResolvedInstrument,
  useCreateListedInstrumentMutation,
  useLazyDiscoverListingsQuery,
  useResolveInstrumentMutation,
  useSearchInstrumentsQuery,
} from "../../../services/instruments";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { onEnter } from "../../../utils/keyboard";
import { buildSelectStyles } from "../SelectInput";
import {
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SEARCH_TOO_SHORT_MESSAGE,
} from "./constants";
import { toErrorMessage } from "./errors";
import {
  listingDescription,
  noListingMessage,
  toDiscoveryState,
} from "./listings";
import { TickerRequestForm } from "./TickerRequestForm";
import {
  AmbiguousSuggestion,
  CreateListedParams,
  InstrumentOption,
  InstrumentSearchOption,
  InstrumentSearchProps,
  PopularInstrumentOption,
} from "./types";
import {
  Disclosure,
  DisclosureField,
  DisclosureHelper,
  DisclosureSelect,
  DisclosureTitle,
  DisclosureToggle,
  ErrorMessage,
  NoPriceBadge,
  OptionMeta,
  OptionRow,
  OptionSymbol,
  SuccessMessage,
  WarningMessage,
  Wrapper,
} from "./styles";

const toOption = (
  instrument: InstrumentSearchResult,
): InstrumentSearchOption => {
  const common = {
    label: `${instrument.symbol} · ${instrument.exchange} — ${instrument.name}`,
    symbol: instrument.symbol,
    exchange: instrument.exchange,
    name: instrument.name,
  };
  return isCatalogueSearchResult(instrument)
    ? {
        ...common,
        value: String(instrument.id),
        instrumentId: instrument.id,
        hasPrice: instrument.latestPrice !== null,
      }
    : {
        ...common,
        value: `popular:${instrument.exchange}:${instrument.symbol}`,
        suggestion: instrument,
      };
};

// Resolve responses are flat and carry `priced` instead of a `latestPrice` object; deriving hasPrice from priced keeps a newly priced ticker from rendering as unpriced.
const resolvedToOption = (
  instrument: ResolvedInstrument,
): InstrumentOption => ({
  value: String(instrument.id),
  label: `${instrument.symbol} · ${instrument.exchange} — ${instrument.name}`,
  instrumentId: instrument.id,
  symbol: instrument.symbol,
  exchange: instrument.exchange,
  name: instrument.name,
  hasPrice: instrument.priced,
});

const adoptLabel = (isCreating: boolean, listing: DiscoveredListing | null) => {
  if (isCreating) {
    return "Adding…";
  }
  return listing ? `Add ${listing.yahooSymbol}` : "Add listing";
};

// A thin Controller over react-select for accessible combobox semantics; search is server-side, so filterOption is disabled, and rate-limited calls start from explicit selections or buttons, never typing.
export const InstrumentSearch = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  disabled = false,
  id,
}: InstrumentSearchProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  const [typedTerm, setTypedTerm] = useState("");
  const debouncedTerm = useDebouncedValue(typedTerm.trim(), SEARCH_DEBOUNCE_MS);
  const [requestSymbol, setRequestSymbol] = useState<string | null>(null);
  const [requestedSymbol, setRequestedSymbol] = useState<string | null>(null);
  const [ambiguous, setAmbiguous] = useState<AmbiguousSuggestion | null>(null);
  const [selectedListing, setSelectedListing] =
    useState<DiscoveredListing | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [pendingOption, setPendingOption] =
    useState<PopularInstrumentOption | null>(null);
  const [suggestionPhase, setSuggestionPhase] = useState<
    "discovering" | "creating" | null
  >(null);

  // Every new intent gets a distinct version, even when the user returns to the same symbol.
  const operationVersion = useRef(0);
  useEffect(
    () => () => {
      operationVersion.current += 1;
    },
    [],
  );

  const { data: results, isFetching } = useSearchInstrumentsQuery(
    debouncedTerm,
    { skip: debouncedTerm.length < MIN_SEARCH_LENGTH },
  );

  const [resolveInstrument] = useResolveInstrumentMutation();
  const [discoverListings] = useLazyDiscoverListingsQuery();
  const [createListedInstrument, { isLoading: isCreating }] =
    useCreateListedInstrumentMutation();

  const options = useMemo(() => (results ?? []).map(toOption), [results]);

  const hasSearched =
    debouncedTerm.length >= MIN_SEARCH_LENGTH &&
    !isFetching &&
    Boolean(results);
  const isRequestOpen = requestSymbol !== null;

  const clearSuggestion = () => {
    operationVersion.current += 1;
    setPendingOption(null);
    setSuggestionPhase(null);
    setAmbiguous(null);
    setSelectedListing(null);
    setSuggestionError(null);
  };

  const handleCreateListed = async ({
    onChange,
    listing,
    identity,
    requestVersion,
  }: CreateListedParams) => {
    setSuggestionError(null);
    try {
      const created = await createListedInstrument({
        ...identity,
        yahooSymbol: listing.yahooSymbol,
      }).unwrap();
      if (operationVersion.current !== requestVersion) {
        return;
      }
      // Select it straight away rather than waiting for the invalidated search to refetch — the
      // user asked for this exact ticker.
      onChange(resolvedToOption(created));
      clearSuggestion();
    } catch (err: unknown) {
      if (operationVersion.current !== requestVersion) {
        return;
      }
      setSuggestionError(toErrorMessage(err));
    }
  };

  const handleChooseListing = (
    onChange: (option: InstrumentOption) => void,
    suggestion: AmbiguousSuggestion,
    listing: DiscoveredListing,
  ) =>
    handleCreateListed({
      onChange,
      listing,
      identity: suggestion.identity,
      requestVersion: ++operationVersion.current,
    });

  const handlePopularSelection = async (
    onChange: (option: InstrumentOption | null) => void,
    option: PopularInstrumentOption,
  ) => {
    clearSuggestion();
    const requestVersion = ++operationVersion.current;
    const isCurrent = () => operationVersion.current === requestVersion;
    const identity = { symbol: option.symbol, exchange: option.exchange };
    // A failed suggestion must stop posing as the field value; the alert carries it.
    const abandonSuggestion = (message: string) => {
      setSuggestionPhase(null);
      setPendingOption(null);
      setSuggestionError(message);
    };
    // A suggestion has no tradeable id. Clear any previous selection before the first await.
    onChange(null);
    setPendingOption(option);
    const { suggestion } = option;
    if ("nativeCurrency" in suggestion) {
      setSuggestionPhase("creating");
      try {
        const resolved = await resolveInstrument({
          symbol: suggestion.symbol,
          exchange: suggestion.exchange,
          name: suggestion.name,
          assetType: suggestion.assetType,
          nativeCurrency: suggestion.nativeCurrency,
        }).unwrap();
        if (isCurrent()) {
          onChange(resolvedToOption(resolved));
          clearSuggestion();
        }
      } catch (err: unknown) {
        if (isCurrent()) {
          abandonSuggestion(toErrorMessage(err));
        }
      }
      return;
    }
    setSuggestionPhase("discovering");

    let discovered: DiscoveredListings;
    try {
      discovered = await discoverListings(identity).unwrap();
    } catch (err: unknown) {
      if (isCurrent()) {
        abandonSuggestion(toErrorMessage(err));
      }
      return;
    }
    if (!isCurrent()) {
      return;
    }

    const next = toDiscoveryState(discovered);
    if (next.status === "none") {
      abandonSuggestion(noListingMessage(identity.symbol, identity.exchange));
      return;
    }
    if (next.status === "ambiguous") {
      setSuggestionPhase(null);
      setAmbiguous({ identity, candidates: next.candidates });
      return;
    }

    setSuggestionPhase("creating");
    await handleCreateListed({
      onChange,
      listing: next.listing,
      identity,
      requestVersion,
    });
    if (isCurrent()) {
      setSuggestionPhase(null);
      setPendingOption(null);
    }
  };

  const toggleRequest = () => {
    clearSuggestion();
    setRequestedSymbol(null);
    setRequestSymbol(isRequestOpen ? null : typedTerm.trim().toUpperCase());
  };

  const handleRequestSubmitted = (symbol: string) => {
    setRequestSymbol(null);
    setRequestedSymbol(symbol);
  };

  const canToggleRequest =
    (hasSearched || isRequestOpen || Boolean(pendingOption)) && !disabled;
  const requestToggleLabel = isRequestOpen
    ? "Cancel request"
    : "Can't find it? Request a ticker";
  const suggestionStatus =
    suggestionPhase === "discovering" ? "Finding listing…" : "Adding ticker…";

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // RHF types the field by its path, which cannot express "this path holds an InstrumentOption" — narrowed once here rather than at every read below.
        const selected = (value as InstrumentOption | null) ?? null;

        // The chooser sits inside the host form (the buy panel), so Enter must adopt the listing instead of submitting the buy.
        const handleListingKeyDown = onEnter(() => {
          if (ambiguous && selectedListing && !isCreating) {
            void handleChooseListing(onChange, ambiguous, selectedListing);
          }
        });

        return (
          <Wrapper>
            <Select<InstrumentSearchOption>
              inputId={inputId}
              aria-invalid={Boolean(error) || undefined}
              aria-errormessage={error ? errorId : undefined}
              isDisabled={disabled}
              isLoading={isFetching || suggestionPhase !== null}
              value={pendingOption ?? selected}
              options={options}
              isOptionDisabled={(option) =>
                suggestionPhase !== null &&
                option.value === pendingOption?.value
              }
              onChange={(option) => {
                setRequestSymbol(null);
                setRequestedSymbol(null);
                if (option && "suggestion" in option) {
                  void handlePopularSelection(onChange, option);
                } else {
                  clearSuggestion();
                  onChange(option);
                }
              }}
              onInputChange={(next, meta) => {
                if (meta.action !== "input-change") {
                  return;
                }
                // Typing abandons a pending or failed suggestion, but never a request the user is filling in.
                clearSuggestion();
                setRequestedSymbol(null);
                setTypedTerm(next);
              }}
              // The catalogue is searched server-side, so react-select must not
              // additionally filter the 20 rows it was handed.
              filterOption={null}
              placeholder="Search a ticker, e.g. VWCE"
              noOptionsMessage={() =>
                debouncedTerm.length < MIN_SEARCH_LENGTH
                  ? SEARCH_TOO_SHORT_MESSAGE
                  : "No instruments found"
              }
              menuPortalTarget={document.body}
              styles={buildSelectStyles<InstrumentSearchOption>({
                hasError: Boolean(error),
                disabled,
              })}
              formatOptionLabel={(option) => (
                <OptionRow>
                  <OptionSymbol>{option.symbol}</OptionSymbol>
                  <OptionMeta>
                    {option.exchange} · {option.name}
                  </OptionMeta>
                  {"hasPrice" in option && !option.hasPrice && (
                    <NoPriceBadge>No price yet</NoPriceBadge>
                  )}
                </OptionRow>
              )}
            />

            {suggestionPhase && (
              <DisclosureHelper role="status">
                {suggestionStatus}
              </DisclosureHelper>
            )}

            {suggestionError && !ambiguous && (
              <ErrorMessage role="alert">{suggestionError}</ErrorMessage>
            )}

            {error && (
              <ErrorMessage id={errorId} role="alert">
                {error.message}
              </ErrorMessage>
            )}

            {selected && !selected.hasPrice && (
              <WarningMessage role="status">
                {selected.symbol} has no price yet, so a buy against it will be
                rejected until a price exists.
              </WarningMessage>
            )}

            {ambiguous && (
              <Disclosure>
                <DisclosureTitle>Choose a listing</DisclosureTitle>
                <WarningMessage role="status">
                  {ambiguous.candidates.length} listings match{" "}
                  {ambiguous.identity.symbol} on {ambiguous.identity.exchange}.
                  Choose the listing you mean.
                </WarningMessage>
                <DisclosureField>
                  Listing *
                  <DisclosureSelect
                    value={selectedListing?.yahooSymbol ?? ""}
                    onChange={(event) =>
                      setSelectedListing(
                        ambiguous.candidates.find(
                          (candidate) =>
                            candidate.yahooSymbol === event.target.value,
                        ) ?? null,
                      )
                    }
                    onKeyDown={handleListingKeyDown}
                  >
                    <option value="" disabled>
                      Choose a listing
                    </option>
                    {ambiguous.candidates.map((candidate) => (
                      <option
                        key={candidate.yahooSymbol}
                        value={candidate.yahooSymbol}
                      >
                        {listingDescription(candidate)}
                      </option>
                    ))}
                  </DisclosureSelect>
                </DisclosureField>
                {suggestionError && (
                  <ErrorMessage role="alert">{suggestionError}</ErrorMessage>
                )}
                <Button
                  type={ButtonTypes.primary}
                  buttonType="button"
                  disabled={!selectedListing || isCreating}
                  onClick={() =>
                    selectedListing &&
                    void handleChooseListing(
                      onChange,
                      ambiguous,
                      selectedListing,
                    )
                  }
                >
                  {adoptLabel(isCreating, selectedListing)}
                </Button>
              </Disclosure>
            )}

            {requestedSymbol && (
              <SuccessMessage role="status">
                Request sent for {requestedSymbol}. We&apos;ll email you once
                it&apos;s been reviewed.
              </SuccessMessage>
            )}

            {canToggleRequest && (
              <DisclosureToggle
                type="button"
                onClick={toggleRequest}
                aria-expanded={isRequestOpen}
              >
                {requestToggleLabel}
              </DisclosureToggle>
            )}

            {requestSymbol !== null && (
              <TickerRequestForm
                initialSymbol={requestSymbol}
                onSubmitted={handleRequestSubmitted}
              />
            )}
          </Wrapper>
        );
      }}
    />
  );
};
